import { LitElement, html, css } from 'lit'
import { store } from '../store.js'
import { navigate } from '../router.js'
import { api } from '../api.js'
import { toast } from './app-toast.js'
import { runTicketAction, pickClientForTicket, pickJobForTicket, pickTaskForTicket, WmjCancelled } from '../wmj-flow.js'
import { STATUSES, STATUS_LABEL } from '../status.js'

// Ticket properties + agent controls (§7.4, §7.7). Clients see a read-only view;
// agents can change status, (re)assign, edit the Workamajig job, and manage CCs.
// Dispatches `ticket-changed` after any mutation so the parent refetches.
class TicketSidebar extends LitElement {
  static properties = {
    ticket: { type: Object },
    isAgent: { type: Boolean },
    work: { type: Object },
    _ccQuery: { state: true },
    _ccResults: { state: true },
    _assigneeQuery: { state: true },
    _assigneeResults: { state: true },
    _groups: { state: true },
    _linkNumber: { state: true },
    _linkRel: { state: true },
    _mergeNumber: { state: true },
    _hours: { state: true },
    _logged: { state: true }
  }

  static styles = css`
    :host { display: block; font-family: var(--font-sans); }
    .card { background: var(--white); border-radius: var(--radius-card); box-shadow: var(--shadow-card); padding: 24px; }
    .field { margin-bottom: 20px; }
    .field:last-child { margin-bottom: 0; }
    .label { font-family: var(--font-eyebrow); font-size: 10.5px; font-weight: 700; letter-spacing: 0.1em;
             text-transform: uppercase; color: var(--label-grey); margin-bottom: 8px; }
    .value { color: var(--eggplant-500); font-size: 14.5px; font-weight: 600; }
    .warn { color: #B4232B; font-size: 12.5px; font-weight: 500; }
    select, input { width: 100%; box-sizing: border-box; height: 44px; font-family: var(--font-sans); font-size: 15px;
             padding: 0 14px; background: var(--white); border: none; border-radius: 0; color: var(--eggplant-500);
             box-shadow: inset 0 0 0 1px var(--eggplant-500); outline: none; }
    input { box-shadow: inset 0 0 0 2px var(--border-input); }
    input:focus { box-shadow: inset 0 0 0 2px var(--eggplant-500); }
    select { appearance: none; -webkit-appearance: none; padding-right: 34px; cursor: pointer;
             background-image: linear-gradient(45deg, transparent 50%, var(--eggplant-500) 50%), linear-gradient(135deg, var(--eggplant-500) 50%, transparent 50%);
             background-position: right 16px center, right 11px center; background-size: 5px 5px, 5px 5px; background-repeat: no-repeat; }
    /* Click-to-open picker fields (client / job): the whole box is the affordance. */
    .picker { cursor: pointer; box-sizing: border-box; height: 44px; display: flex; align-items: center;
             font-family: var(--font-sans); font-size: 15px; color: var(--eggplant-500); padding: 0 14px;
             background: var(--white); box-shadow: inset 0 0 0 1px var(--eggplant-500); }
    .picker:hover { box-shadow: inset 0 0 0 2px var(--eggplant-500); }
    .picker.unset { color: #B4232B; box-shadow: inset 0 0 0 1px #B4232B; }
    .row { display: flex; gap: 8px; margin-top: 10px; align-items: center; }
    button { font: inherit; cursor: pointer; padding: 0; border: none; background: none;
             font-size: 13px; font-weight: 700; color: var(--sky-500); text-decoration: underline; text-underline-offset: 3px; }
    button:hover { color: var(--sky-700); }
    .cc { display: flex; justify-content: space-between; align-items: center; font-size: 14px; padding: 3px 0; color: var(--eggplant-500); }
    .cc button { color: var(--neutral-500); text-decoration: none; font-weight: 400; font-size: 14px; }
    .cc button:hover { color: #B4232B; }
    .results { box-shadow: inset 0 0 0 1px var(--border-input); margin-top: 8px; }
    .results div { padding: 8px 12px; cursor: pointer; font-size: 14px; color: var(--eggplant-500); }
    .results div:hover { background: var(--neutral-50); }
    .link { color: var(--sky-500); cursor: pointer; text-decoration: underline; text-underline-offset: 3px; font-weight: 600; }
    /* Workamajig task (§19.7): the estimate, where it came from, and its write state. */
    .badge { display: inline-block; font-family: var(--font-eyebrow); font-size: 9.5px; font-weight: 700;
             letter-spacing: 0.08em; text-transform: uppercase; padding: 2px 6px; margin-left: 6px;
             color: var(--label-grey); box-shadow: inset 0 0 0 1px var(--border-input); vertical-align: 2px; }
    .badge.ai { color: var(--sky-700); box-shadow: inset 0 0 0 1px var(--sky-500); }
    .muted { color: var(--neutral-500); font-size: 13px; font-weight: 500; }
    .hours { display: flex; gap: 8px; align-items: center; margin-top: 10px; }
    .hours input { width: 90px; flex: 0 0 auto; }
    /* Worklog (§20.2): today's time on this ticket, and which tier produced it. */
    .badge.measured { color: var(--sky-700); box-shadow: inset 0 0 0 1px var(--sky-500); }
  `

  constructor() {
    super()
    this._ccQuery = ''
    this._ccResults = []
    this._assigneeQuery = ''
    this._assigneeResults = []
    this._groups = []
    this._linkNumber = ''
    this._linkRel = 'related'
    this._mergeNumber = ''
    this._hours = null          // non-null while the estimate is being edited
    this._logged = null         // non-null while logging time by hand
  }

  connectedCallback() {
    super.connectedCallback()
    // Groups back the queue assignment select (agents only; few enough to load once).
    if (this.isAgent) api.get('/groups').then(r => { this._groups = r.groups }).catch(() => {})
  }

  _changed() { this.dispatchEvent(new CustomEvent('ticket-changed', { bubbles: true, composed: true })) }

  _setGroup(e) {
    const groupId = e.target.value || null
    this._act(() => api.post(`/tickets/${this.ticket.number}/group`, { groupId }), 'Group updated')
  }

  // Client / job are picked from Workamajig via the modal (§18): clicking the field
  // opens the picker, choosing a record closes it and saves. Cancelling leaves the
  // ticket unchanged.
  async _pickClient() {
    try {
      if (await pickClientForTicket({ ticketNumber: this.ticket.number, client: this.ticket.client })) this._changed()
    } catch (err) {
      toast(err.message || 'Failed to set client', 'error')
    }
  }

  async _pickJob() {
    if (!this.ticket.client?.workamajigCompanyId) { toast('Select a client first', 'info'); return }
    try {
      if (await pickJobForTicket({ ticketNumber: this.ticket.number, client: this.ticket.client })) this._changed()
    } catch (err) {
      toast(err.message || 'Failed to assign job', 'error')
    }
  }

  // The task is the budget line the ticket's hours belong to (§19.1) — scoped to the
  // job, so the job comes first.
  async _pickTask() {
    if (!this.ticket.workamajigJobNumber) { toast('Assign a job first', 'info'); return }
    try {
      if (await pickTaskForTicket({ ticketNumber: this.ticket.number, jobNumber: this.ticket.workamajigJobNumber })) this._changed()
    } catch (err) {
      toast(err.message || 'Failed to set the task', 'error')
    }
  }

  // Agent actions run through runTicketAction so first-touch Workamajig linking
  // (§18.3) is resolved via the picker modal and the action retried. `thunk` must
  // be a function returning the api call (it may run more than once).
  async _act(thunk, okMsg) {
    try {
      await runTicketAction(thunk, {
        ticketNumber: this.ticket.number, client: this.ticket.client, jobNumber: this.ticket.workamajigJobNumber
      })
      if (okMsg) toast(okMsg, 'success')
      this._changed()
    } catch (err) {
      // On cancel, still refresh — a company may have been linked before aborting.
      if (err instanceof WmjCancelled) { this._changed(); return }
      toast(err.message || 'Action failed', 'error')
    }
  }

  _setStatus(e) {
    const status = e.target.value
    this._act(() => api.post(`/tickets/${this.ticket.number}/status`, { status }))
  }

  _assignMe() {
    const me = store.get().user?.id
    this._act(() => api.post(`/tickets/${this.ticket.number}/assign`, { assigneeId: me }), 'Assigned to you')
  }

  // Typeahead over agents so a ticket can be assigned to anyone, not just self (§7.7).
  async _searchAssignee(e) {
    this._assigneeQuery = e.target.value
    if (this._assigneeQuery.trim().length < 2) { this._assigneeResults = []; return }
    try { this._assigneeResults = await api.get(`/users?query=${encodeURIComponent(this._assigneeQuery.trim())}&role=agent`) }
    catch { this._assigneeResults = [] }
  }

  _assign(userId) {
    this._assigneeQuery = ''; this._assigneeResults = []
    this._act(() => api.post(`/tickets/${this.ticket.number}/assign`, { assigneeId: userId }), 'Assignee updated')
  }

  async _searchCc(e) {
    this._ccQuery = e.target.value
    if (this._ccQuery.trim().length < 2) { this._ccResults = []; return }
    try { this._ccResults = await api.get(`/users?query=${encodeURIComponent(this._ccQuery.trim())}`) }
    catch { this._ccResults = [] }
  }

  // A CC can be a known user or any email address — an unknown address is created as
  // a lightweight account server-side so those people receive the thread and their
  // emailed replies land on the ticket.
  get _ccIsEmail() { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this._ccQuery.trim()) }

  _ccKeydown(e) {
    if (e.key === 'Enter' && this._ccIsEmail) { e.preventDefault(); this._addCcEmail() }
  }

  _addCc(userId) {
    this._ccQuery = ''; this._ccResults = []
    this._act(() => api.post(`/tickets/${this.ticket.number}/cc`, { userId }), 'CC added')
  }

  _addCcEmail() {
    const email = this._ccQuery.trim()
    this._ccQuery = ''; this._ccResults = []
    this._act(() => api.post(`/tickets/${this.ticket.number}/cc`, { email }), 'CC added')
  }

  _removeCc(userId) {
    this._act(() => api.delete(`/tickets/${this.ticket.number}/cc/${userId}`), 'CC removed')
  }

  // ---- Workamajig booking (§19.7) ------------------------------------------

  // The estimate is advisory — it never goes to Workamajig — so correcting it only
  // sharpens what this ticket claims, and the calibration behind the next estimate.
  async _saveHours() {
    const estHours = Number(this._hours)
    if (!Number.isFinite(estHours) || estHours <= 0) { toast('Enter hours as a number', 'info'); return }
    try {
      await api.patch(`/tickets/${this.ticket.number}/wmj-task`, { estHours })
      this._hours = null
      toast('Estimate updated', 'success')
      this._changed()
    } catch (err) {
      toast(err.message || 'Failed to update the estimate', 'error')
    }
  }

  async _retryBooking() {
    try {
      await api.post(`/tickets/${this.ticket.number}/wmj-task/retry`, {})
      this._changed()
    } catch (err) {
      toast(err.message || 'Retry failed', 'error')
    }
  }

  // Where the number came from, so nobody mistakes a fallback for a judgement.
  _estBadge(b) {
    if (b.estSource === 'ai') return html`<span class="badge ai" title=${b.estBasis || 'Estimated by AI'}>AI</span>`
    if (b.estSource === 'manual') return html`<span class="badge">edited</span>`
    return html`<span class="badge" title="No AI estimate was available; this is the configured default">default</span>`
  }

  _renderEstimate(b) {
    if (b.estHours == null) return ''
    return this._hours === null
      ? html`
        <div class="value">${b.estHours}h estimated${this._estBadge(b)}</div>
        <div class="row"><button @click=${() => (this._hours = String(b.estHours ?? ''))}>Adjust hours</button></div>`
      : html`
        <div class="hours">
          <input type="number" step="0.25" min="0.25" .value=${this._hours}
            @input=${e => (this._hours = e.target.value)} />
          <button @click=${this._saveHours}>Save</button>
          <button @click=${() => (this._hours = null)}>Cancel</button>
        </div>`
  }

  // The booking is the assignee's presence on the chosen task. Its state matters only
  // when something went wrong — otherwise the estimate is the interesting part.
  _renderBooking(b) {
    const status = b.state === 'booked'
      ? html`<div class="muted">${b.assignee?.name || 'The assignee'} ${b.alreadyAssigned ? 'was already on' : 'added to'} this task in Workamajig</div>`
      : b.state === 'failed'
        ? html`
          <div class="warn">Not booked in Workamajig — ${b.lastError || 'the write failed'}</div>
          <div class="row"><button @click=${this._retryBooking}>Retry</button></div>`
        : b.state === 'skipped'
          ? html`<div class="muted">No booking needed for this ticket</div>`
          : html`<div class="muted">Booking${b.state === 'deferred' ? ' — waiting for a task' : ''}…</div>`
    return html`${this._renderEstimate(b)}${status}`
  }

  // ---- Worklog (§20.2) -----------------------------------------------------

  // Where today's number came from. `measured` is real attention on the ticket,
  // `derived` is inferred from what was written, `manual` is what the agent said —
  // the distinction is the whole point, so it is on the screen rather than implied.
  _tierBadge(source) {
    if (source === 'manual') return html`<span class="badge" title="You entered this">entered</span>`
    if (source === 'measured') {
      return html`<span class="badge measured" title="Measured while you had this ticket open">measured</span>`
    }
    return html`<span class="badge" title="Estimated from the replies and notes you wrote today">derived</span>`
  }

  // Hours the agent asserts, which beat both automatic tiers. Clearing the field
  // hands the ticket back to them rather than pinning it at zero.
  async _saveLogged() {
    const raw = String(this._logged).trim()
    const hours = raw === '' ? null : Number(raw)
    if (hours !== null && (!Number.isFinite(hours) || hours < 0)) {
      toast('Enter hours as a number, or clear the field', 'info')
      return
    }
    try {
      const totals = await api.put(`/worklog/tickets/${this.ticket.number}/hours`, { hours })
      this._logged = null
      this.dispatchEvent(new CustomEvent('work-changed', { detail: totals, bubbles: true, composed: true }))
      toast(hours === null ? 'Time cleared' : 'Time logged', 'success')
    } catch (err) {
      toast(err.message || 'Failed to log time', 'error')
    }
  }

  _renderWork() {
    const w = this.work
    if (!w) return ''
    return html`
      <div class="field">
        <div class="label">Your time today</div>
        ${this._logged === null
          ? html`
            <div class="value">${w.hours}h${w.hoursSource ? this._tierBadge(w.hoursSource) : ''}</div>
            ${w.manualNote ? html`<div class="muted">${w.manualNote}</div>` : ''}
            <div class="muted">${w.dayHours}h across every ticket today</div>
            <div class="row">
              <button @click=${() => (this._logged = String(w.manualHours ?? ''))}>
                ${w.manualHours != null ? 'Change' : 'Log time'}
              </button>
            </div>`
          : html`
            <div class="hours">
              <input type="number" step="0.25" min="0" placeholder="hours" .value=${this._logged}
                @input=${e => (this._logged = e.target.value)} />
              <button @click=${this._saveLogged}>Save</button>
              <button @click=${() => (this._logged = null)}>Cancel</button>
            </div>
            <div class="muted">Leave it empty to go back to the measured time.</div>`}
      </div>`
  }

  // ---- Merge & link (§7.8) — agent only ------------------------------------

  async _addLink() {
    const number = parseInt(this._linkNumber, 10)
    if (!number) { toast('Enter a ticket number to link', 'info'); return }
    this._linkNumber = ''
    try {
      await api.post(`/tickets/${this.ticket.number}/links`, { number, relationship: this._linkRel })
      this._changed()
    } catch (err) {
      toast(err.message || 'Failed to link ticket', 'error')
    }
  }

  async _removeLink(id) {
    try {
      await api.delete(`/tickets/${this.ticket.number}/links/${id}`)
      this._changed()
    } catch (err) {
      toast(err.message || 'Failed to remove link', 'error')
    }
  }

  async _merge() {
    const targetNumber = parseInt(this._mergeNumber, 10)
    if (!targetNumber) { toast('Enter the canonical ticket number', 'info'); return }
    if (!confirm(`Merge #${this.ticket.number} into #${targetNumber}? Its messages move there and #${this.ticket.number} is closed.`)) return
    try {
      const canonical = await api.post(`/tickets/${this.ticket.number}/merge`, { targetNumber })
      toast(`Merged into #${canonical.number}`, 'success')
      navigate(`/tickets/${canonical.number}`)   // follow the conversation to its new home
    } catch (err) {
      toast(err.message || 'Failed to merge', 'error')
    }
  }

  render() {
    const t = this.ticket
    if (!t) return html``
    return html`
      <div class="card">
        <div class="field">
          <div class="label">Status</div>
          ${this.isAgent ? html`
            <select @change=${this._setStatus}>
              ${STATUSES.map(s => html`<option value=${s} ?selected=${t.status === s}>${STATUS_LABEL[s]}</option>`)}
            </select>` : html`<div class="value">${STATUS_LABEL[t.status]}</div>`}
        </div>

        <div class="field">
          <div class="label">Assignee</div>
          <div class="value">${t.assignee ? (t.assignee.name || t.assignee.email) : 'Unassigned'}</div>
          ${this.isAgent ? html`
            <input placeholder="Assign to agent by name/email…" .value=${this._assigneeQuery} @input=${this._searchAssignee} />
            ${this._assigneeResults.length ? html`<div class="results">
              ${this._assigneeResults.map(u => html`<div @click=${() => this._assign(u.id)}>${u.fullName || u.email}</div>`)}
            </div>` : ''}
            <div class="row">
              <button @click=${this._assignMe}>Assign to me</button>
            </div>` : ''}
        </div>

        <div class="field">
          <div class="label">Group queue</div>
          ${this.isAgent ? html`
            <select @change=${this._setGroup}>
              <option value="" ?selected=${!t.group}>No group</option>
              ${this._groups.map(g => html`<option value=${g.id} ?selected=${t.group?.id === g.id}>${g.name}</option>`)}
            </select>` : html`<div class="value">${t.group ? t.group.name : '—'}</div>`}
        </div>

        <div class="field">
          <div class="label">Requester</div>
          <div class="value">${t.requester.name || t.requester.email}</div>
        </div>

        <div class="field">
          <div class="label">Client</div>
          ${this.isAgent ? html`
            <div class="picker ${t.client?.workamajigCompanyId ? '' : 'unset'}" @click=${this._pickClient}>
              ${t.client?.workamajigCompanyId ? t.client.name : 'Select Client…'}
            </div>
          ` : html`
            <div class="value">
              ${t.client ? t.client.name : '—'}
              ${t.client && !t.client.workamajigCompanyId ? html`<span class="warn"> · not linked to Workamajig</span>` : ''}
            </div>`}
        </div>

        <div class="field">
          <div class="label">Workamajig job</div>
          ${this.isAgent ? html`
            <div class="picker ${t.workamajigJobNumber ? '' : 'unset'}" @click=${this._pickJob}>
              ${t.workamajigJobNumber ? `#${t.workamajigJobNumber}` : 'Select Job…'}
            </div>
          ` : html`<div class="value">${t.workamajigJobNumber || '—'}</div>`}
        </div>

        <div class="field">
          <div class="label">Workamajig task</div>
          ${this.isAgent ? html`
            <div class="picker ${t.workamajigTask ? '' : 'unset'}" @click=${this._pickTask}>
              ${t.workamajigTask ? t.workamajigTask.name : 'Select Task…'}
            </div>
          ` : html`<div class="value">${t.workamajigTask?.name || '—'}</div>`}
          ${this.isAgent && t.wmjBooking ? this._renderBooking(t.wmjBooking) : ''}
        </div>

        ${this.isAgent ? this._renderWork() : ''}

        <div class="field">
          <div class="label">CC</div>
          ${t.ccs.length ? t.ccs.map(c => html`
            <div class="cc"><span>${c.name || c.email}</span>
              ${this.isAgent ? html`<button @click=${() => this._removeCc(c.id)}>✕</button>` : ''}</div>
          `) : html`<div class="value">None</div>`}
          ${this.isAgent ? html`
            <input placeholder="Add CC by name or email…" .value=${this._ccQuery}
              @input=${this._searchCc} @keydown=${this._ccKeydown} />
            ${this._ccResults.length || this._ccIsEmail ? html`<div class="results">
              ${this._ccResults.map(u => html`<div @click=${() => this._addCc(u.id)}>${u.fullName || u.email} · ${u.role}</div>`)}
              ${this._ccIsEmail && !this._ccResults.some(u => (u.email || '').toLowerCase() === this._ccQuery.trim().toLowerCase())
                ? html`<div @click=${this._addCcEmail}>Add “${this._ccQuery.trim()}”…</div>` : ''}
            </div>` : ''}` : ''}
        </div>

        ${this.isAgent && t.satisfaction ? html`
          <div class="field">
            <div class="label">Satisfaction</div>
            <div class="value">
              ${t.satisfaction.rating
                ? html`${t.satisfaction.rating === 'good' ? '👍 Good' : '👎 Bad'}${t.satisfaction.comment ? html` — “${t.satisfaction.comment}”` : ''}`
                : 'Prompt sent · awaiting rating'}
            </div>
          </div>` : ''}

        ${t.mergedInto ? html`
          <div class="field">
            <div class="label">Merged</div>
            <div class="value">This ticket was merged into
              <a class="link" @click=${() => navigate(`/tickets/${t.mergedInto.number}`)}>#${t.mergedInto.number}</a>.</div>
          </div>` : ''}

        <div class="field">
          <div class="label">Related tickets</div>
          ${t.links && t.links.length ? t.links.map(l => html`
            <div class="cc">
              <span><a class="link" @click=${() => navigate(`/tickets/${l.number}`)}>#${l.number}</a>
                · ${l.relationship}</span>
              ${this.isAgent ? html`<button @click=${() => this._removeLink(l.id)}>✕</button>` : ''}
            </div>`) : html`<div class="value">None</div>`}
          ${this.isAgent && !t.mergedInto ? html`
            <div class="row">
              <input placeholder="Ticket #" .value=${this._linkNumber}
                @input=${e => (this._linkNumber = e.target.value)} style="flex:1" />
              <select @change=${e => (this._linkRel = e.target.value)}>
                <option value="related">related</option>
                <option value="duplicate">duplicate</option>
              </select>
              <button @click=${this._addLink}>Link</button>
            </div>
            <div class="row">
              <input placeholder="Merge into #" .value=${this._mergeNumber}
                @input=${e => (this._mergeNumber = e.target.value)} style="flex:1" />
              <button @click=${this._merge}>Merge</button>
            </div>` : ''}
        </div>
      </div>
    `
  }
}

customElements.define('ticket-sidebar', TicketSidebar)
